import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
  Legend,
} from 'recharts';
import { Card, CardHeader, InlineStat, StatTile, TrendPill } from '../../../components/ui/Card';
import { Table, THead, Th, Td, Tr, Nil, TTotalRow, TableFooter } from '../../../components/ui/Table';
import { Badge } from '../../../components/ui/Badge';
import {
  BarListChart,
  ChartEmpty,
  ChartSubtitle,
  CHART_AXIS,
  CHART_GRID,
  CHART_HEIGHT,
  CHART_LEGEND,
  CHART_SERIES,
  CHART_TOOLTIP,
  RAMP_INK,
} from '../../../components/ui/chart';

/** Event markers (the relaunch) are annotations, not data: quiet stone, never the accent. */
const MARKER = RAMP_INK[2];
import { formatDayMonth, formatDayMonthYear } from '../../../utils/dateFormat';
import { fmtWeek } from '../../Marketing/search-visibility/format';
import type { GtmBoardData } from '../../../api/gtm';
import { fmtNumber, fmtCurrency, fmtPercent, fmtDelta, fmtSigned, fmtScorecard, fmtComparisonLabel, countryName, mostlyUnclassifiedLabel, endsAverageChange, withTargetMarkets } from './components/format';
import { PairRow } from './MarketingView';

/**
 * The board's panels, exported one by one so Marketing → Overview can lead with the
 * few that matter (KPIs, organic trend, AI visibility) and tuck the rest into
 * "More detail" via {@link BoardDetail}.
 */
export function BoardDetail({ data }: { data: GtmBoardData }) {
  // Brand vs category is tall; a short pipeline table beside it left a half-empty cell.
  return (
    <div className="space-y-6">
      <BrandSplit data={data} />
      <AiCompetitors data={data} />
      <PairRow
        sizes={[data.icpByVertical?.rows.length ?? 0, data.markets?.length ?? 0]}
        left={<IcpByVertical data={data} />}
        right={<Markets data={data} />}
      />
    </div>
  );
}

const axisDate = (d: unknown) => formatDayMonth(String(d));
const tooltipDate = (d: unknown) => formatDayMonthYear(String(d));

/** Search Console finalises a day about two days later: anything newer is still filling in. */
const GSC_SETTLE_DAYS = 2;

function isoDaysAgo(days: number): string {
  return new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
}

/**
 * Daily series → trailing 7-day average per day, with the days Search Console
 * hasn't finished counting left off the end (they read as a false drop). The
 * first six days average what there is so far.
 */
function sevenDayAverage<T extends { date: string }, K extends keyof T & string>(points: T[], keys: K[], days = 7): Array<{ date: string } & Record<K, number>> {
  const settled = isoDaysAgo(GSC_SETTLE_DAYS);
  const full = points.filter((p) => p.date < settled);
  return full.map((p, i) => {
    const window = full.slice(Math.max(0, i - (days - 1)), i + 1);
    const row = { date: p.date } as { date: string } & Record<K, number>;
    for (const k of keys) {
      (row as Record<string, number>)[k] = window.reduce((n, w) => n + Number(w[k] ?? 0), 0) / window.length;
    }
    return row;
  });
}

/** True when an event date falls inside the charted days, so its marker can be drawn. */
function inRange(date: string | null | undefined, points: { date: string }[]): date is string {
  if (!date || points.length === 0) return false;
  return date >= points[0]!.date && date <= points[points.length - 1]!.date;
}

/** Where each headline scorecard comes from, for the tile's (i). */
const SCORECARD_SOURCE: Record<string, string> = {
  organic_clicks: 'Search Console',
  organic_impressions: 'Search Console',
  organic_ctr: 'Search Console',
  sourced_pipeline: 'Attio',
  demos_booked: 'Calendly, the same count Sales shows',
};

/** Tile names where the API's label says less than it should. */
const TILE_LABEL: Record<string, string> = {
  sourced_pipeline: 'Inbound-sourced pipeline',
};

/** How each tile is counted, for its (i). */
const TILE_METHOD: Record<string, string> = {
  sourced_pipeline:
    'New-business deals opened in the period (open + won) with a tracked inbound conversion behind them: a website, LinkedIn or Calendly form from anyone at the company in the 12 months before, with no rep outreach earlier. Deals whose Lead Source or Account Source in Attio merely says inbound, with no tracked conversion, are not in this figure; they are shown apart as “claimed”.',
  organic_clicks:
    'Every Search Console click, including queries Google anonymises. Brand vs category can only split the clicks on named queries, so it shows fewer; it says how many.',
  demos_booked:
    'Demo meetings only (not discovery, pricing or 30-min calls), tests left out, a rescheduled booking once, and the same person rebooking within 30 days once. A demo canceled later still counts here; the MQL card leaves canceled demos out.',
};

export function BoardKpis({
  data,
  periodLabel,
  requestDemoFormLeads,
}: {
  data: GtmBoardData;
  periodLabel: string;
  /** People who sent the "Request demo" website form in the period (the funnel's count), to set apart from Calendly bookings. */
  requestDemoFormLeads?: number | null;
}) {
  if (data.scorecards.length === 0) {
    return null;
  }
  const created = data.sourcedPipeline ? pipelineTotals(data.sourcedPipeline).pipeline : null;
  // Fill the row: as many columns as there are tiles, up to five.
  const cols = ['', 'lg:grid-cols-1', 'lg:grid-cols-2', 'lg:grid-cols-3', 'lg:grid-cols-4', 'lg:grid-cols-5'][
    Math.min(data.scorecards.length, 5)
  ];
  return (
    <div className={`grid grid-cols-2 md:grid-cols-3 ${cols} gap-4`}>
      {data.scorecards.map((s) => {
        const delta = fmtDelta(s.deltaPercent);
        const weekly = s.weeklyAverage != null;
        const baseline = fmtDelta(s.vsBaselinePercent ?? null);
        // One short line at the foot: the figure the headline is part of. The
        // period sits in the range chips and the source in the (i), not on the tile.
        const context =
          s.key === 'sourced_pipeline' && created !== null
            ? `of ${fmtScorecard(created, 'currency')} created`
            : weekly && s.unit === 'count'
              ? `${fmtNumber(s.value)} in total`
              : s.detail ?? null;
        return (
          <StatTile
            key={s.key}
            label={TILE_LABEL[s.key] ?? s.label}
            value={fmtScorecard(weekly ? s.weeklyAverage! : s.value, s.unit)}
            unit={weekly ? '/wk' : undefined}
            // No previous figure (e.g. Sales' table doesn't reach back that far): no row rather than a bare dash.
            compare={[
              ...(s.deltaPercent == null ? [] : [{ value: delta.text, trend: delta.trend, against: fmtComparisonLabel(s.periodDays) }]),
              ...(s.vsBaselinePercent == null || Number.isNaN(s.vsBaselinePercent)
                ? []
                : [{ value: baseline.text, trend: baseline.trend, against: 'vs pre-launch' }]),
            ]}
            sub={context ?? undefined}
            info={
              <div className="space-y-1.5">
                <p>
                  {periodLabel}, from {SCORECARD_SOURCE[s.key] ?? 'the data spine'}.
                </p>
                {TILE_METHOD[s.key] && <p>{TILE_METHOD[s.key]}</p>}
                {s.key === 'sourced_pipeline' && data.dealSources && data.dealSources.claimedDeals > 0 && (
                  <p>
                    Claimed inbound by a sales field, no tracked conversion: {fmtScorecard(data.dealSources.claimedValue, 'currency')} across{' '}
                    {fmtNumber(data.dealSources.claimedDeals)} deals, not counted above.
                  </p>
                )}
                {s.key === 'demos_booked' && requestDemoFormLeads != null && (
                  <p>
                    The {fmtNumber(requestDemoFormLeads)} “Request demo” website form leads are a separate count (one route in),
                    shown in the funnel.
                  </p>
                )}
              </div>
            }
          />
        );
      })}
    </div>
  );
}

/** Below this many daily points the chart shows days as they are, not a 7-day average. */
const SMOOTH_MIN_POINTS = 14;

export function SearchTrend({ data }: { data: GtmBoardData }) {
  const raw = data.searchTrend?.points ?? [];
  // A 7-day average needs more than a week to smooth: on the 7-day chip (5 settled days) it is plain daily figures.
  const smoothed = raw.length > SMOOTH_MIN_POINTS;
  const points = sevenDayAverage(raw, ['clicks', 'impressions'], smoothed ? 7 : 1);
  const relaunch = data.searchTrend?.relaunchDate;
  const relaunchDate = inRange(relaunch, points) ? relaunch : null;
  // Clicks and impressions differ by two orders of magnitude, so they get a chart
  // each with its own axis rather than one chart with two.
  const chart = (key: 'clicks' | 'impressions', label: string, color: string) => (
    <div>
      <ChartSubtitle>{label}</ChartSubtitle>
      <ResponsiveContainer width="100%" height={CHART_HEIGHT.small}>
        <LineChart data={points} margin={{ top: 16, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid {...CHART_GRID} />
          <XAxis dataKey="date" {...CHART_AXIS} minTickGap={32} tickFormatter={axisDate} />
          <YAxis {...CHART_AXIS} width={44} tickFormatter={(v: number) => fmtNumber(v)} />
          <Tooltip {...CHART_TOOLTIP} labelFormatter={tooltipDate} formatter={(v) => `${fmtNumber(Math.round(Number(v)))} a day${smoothed ? ' (7-day avg.)' : ''}`} />
          {relaunchDate && (
            <ReferenceLine
              x={relaunchDate}
              stroke={MARKER}
              strokeDasharray="4 4"
              label={{ value: 'Relaunch', position: 'top', fontSize: 10, fill: MARKER }}
            />
          )}
          <Line type="linear" dataKey={key} name={label} stroke={color} dot={false} strokeWidth={2} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );

  return (
    <Card>
      <CardHeader
        title="Organic search"
        description={`Clicks and impressions per day${smoothed ? ', 7-day average' : ''}, from Search Console${
          relaunchDate ? `; relaunch on ${formatDayMonthYear(relaunchDate)} marked` : ''
        }. The last ${GSC_SETTLE_DAYS} days are left off: Search Console is still counting them.`}
      />
      {points.length === 0 ? (
        <ChartEmpty label="No Search Console data for this period." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {chart('clicks', 'Clicks', CHART_SERIES[0])}
          {chart('impressions', 'Impressions', CHART_SERIES[0])}
        </div>
      )}
    </Card>
  );
}

/** Ratio (0-1) → "12.3%", or "—" when null. */
function pctOrDash(ratio: number | null, digits = 1): string {
  return ratio === null ? '—' : fmtPercent(ratio * 100, digits);
}

/**
 * Change across the period, in percentage points: the average of the last 7
 * daily readings against the first 7, as Search Visibility compares a
 * period's ends. Until 12 Oct it took the first and last single days, so a 90-day
 * chart that runs from about 7% to 10% read "+8.7pp".
 */
function trendChange(points: { date: string; visibility: number | null }[]): { value: string; windows: string; trend: 'up' | 'down' | 'neutral'; title: string } | null {
  const c = endsAverageChange(points.map((p) => p.visibility));
  if (!c) return null;
  const rounded = Number((c.change * 100).toFixed(1));
  // The two windows in dates, as the Search Visibility tiles name theirs ("6–12 Oct vs 16–22 Jul").
  const dated = points.filter((p) => p.visibility !== null && Number.isFinite(p.visibility));
  const span = (xs: typeof dated) =>
    xs.length === 0 ? '' : xs.length === 1 ? formatDayMonth(xs[0]!.date) : fmtWeek(xs[0]!.date, xs[xs.length - 1]!.date);
  const windows = `${span(dated.slice(-c.days))} vs ${span(dated.slice(0, c.days))} · ${c.days}-day averages`;
  return {
    value: fmtSigned(rounded, 1, 'pp'),
    windows,
    trend: rounded > 0 ? 'up' : rounded < 0 ? 'down' : 'neutral',
    title: `Average of the first ${c.days} days ${fmtPercent(c.first * 100)} → last ${c.days} days ${fmtPercent(c.last * 100)} (Peec)`,
  };
}

export function AiVisibility({ data }: { data: GtmBoardData }) {
  if (!data.authority) {
    return (
      <Card>
        <CardHeader title="AI visibility" description="How often AI engines mention SEQUESTO (Peec)" />
        <ChartEmpty label="No Peec AI-visibility data for this period." />
      </Card>
    );
  }
  const a = data.authority;
  // Most tracked engines never mention us; listing each at 0% buries the few that do,
  // so only engines that mention us get a bar.
  const engines = a.perEngine.filter((e) => (e.visibility ?? 0) > 0);
  // One ink step per engine, strongest first, so no two bars share a colour.
  const engineBars = engines
    .map((e) => ({ label: e.engineLabel, value: (e.visibility ?? 0) * 100 }))
    .sort((x, y) => y.value - x.value)
    .map((e, i) => ({ ...e, color: RAMP_INK[Math.min(i, RAMP_INK.length - 2)] }));
  const change = trendChange(a.trend);

  return (
    <Card>
      <CardHeader title="AI visibility" description="How often AI engines mention SEQUESTO, over the selected period (Peec)" />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-6 mb-6">
        <div className="min-w-0">
          <InlineStat label="AI visibility" value={pctOrDash(a.visibility)} />
          {change && change.trend !== 'neutral' && (
            <div title={change.title}>
              <TrendPill trend={change.trend} className="mt-1.5">
                {change.value}
              </TrendPill>
              <p className="mt-1 text-body-xs text-text-tertiary">{change.windows}</p>
            </div>
          )}
        </div>
        <InlineStat label="AI share of voice" value={pctOrDash(a.shareOfVoice)} />
        <InlineStat label="Google AI Overview" value={pctOrDash(a.aiOverviewVisibility)} />
        <InlineStat label="Sentiment" value={a.sentiment === null ? '—' : Math.round(a.sentiment)} sub="0–100" />
        <InlineStat
          label="Citations"
          value={fmtNumber(a.citation.citationCount)}
          sub={
            a.citation.domains && a.citation.domains.length > 0
              ? `of ${a.citation.domains.join(', ')} in AI answers`
              : `of our own ${a.citation.sourceCount === 1 ? 'domain' : `${fmtNumber(a.citation.sourceCount)} domains`} in AI answers`
          }
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <ChartSubtitle>AI visibility over time</ChartSubtitle>
          {a.trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={CHART_HEIGHT.small}>
              <LineChart
                data={a.trend.map((p) => ({ date: p.date, visibility: p.visibility === null ? null : p.visibility * 100 }))}
                margin={{ top: 16, right: 8, bottom: 0, left: 0 }}
              >
                <CartesianGrid {...CHART_GRID} />
                <XAxis dataKey="date" {...CHART_AXIS} minTickGap={32} tickFormatter={axisDate} />
                <YAxis {...CHART_AXIS} width={44} tickFormatter={pctTick} />
                <Tooltip {...CHART_TOOLTIP} labelFormatter={tooltipDate} formatter={(v) => `${Number(v).toFixed(1)}%`} />
                {inRange(a.relaunchDate, a.trend) && (
                  <ReferenceLine
                    x={a.relaunchDate}
                    stroke={MARKER}
                    strokeDasharray="4 4"
                    label={{ value: 'Relaunch', position: 'top', fontSize: 10, fill: MARKER }}
                  />
                )}
                <Line
                  type="linear"
                  dataKey="visibility"
                  name="AI visibility"
                  stroke={CHART_SERIES[0]}
                  dot={false}
                  strokeWidth={2}
                  connectNulls
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <ChartEmpty label="No trend data." />
          )}
        </div>

        <div>
          <ChartSubtitle>Engines that mention us</ChartSubtitle>
          {engineBars.length > 0 ? (
            <BarListChart items={engineBars} formatValue={pctTick} labelWidth="w-32" axisTicks={4} />
          ) : (
            <ChartEmpty label="No engine mentions us yet." />
          )}
        </div>
      </div>
    </Card>
  );
}

const pctTick = (v: number) => `${Math.round(v)}%`;

function AiCompetitors({ data }: { data: GtmBoardData }) {
  const a = data.authority;
  if (!a) return null;
  const markets = withTargetMarkets(a.byMarket);
  // A market at exactly 0 is real (prompts ran, no mention), not a gap: said under the table.
  const unmentioned = markets.filter((m) => m.visibility === 0);
  const untracked = markets.filter((m) => m.visibility === null);
  const competitorBars = a.competitors.map((c) => ({
    label: c.brandName,
    value: c.visibility === null ? 0 : c.visibility * 100,
    highlight: c.isOwn,
  }));
  return (
    <Card>
      <CardHeader title="AI visibility vs competitors and by market" description="Peec" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <ChartSubtitle>AI visibility vs competitors</ChartSubtitle>
          {competitorBars.length > 0 ? (
            <BarListChart
              items={competitorBars}
              colorMode="highlight"
              // Ink, not the series indigo: indigo is for links, focus and selection.
              color={RAMP_INK[0]}
              formatValue={pctTick}
              labelWidth="w-28"
              axisTicks={4}
            />
          ) : (
            <ChartEmpty label="No competitor data." />
          )}
        </div>
        <div>
          <ChartSubtitle>By market</ChartSubtitle>
          {markets.length > 0 ? (
            <>
            <Table>
              <THead>
                <tr>
                  <Th>Market</Th>
                  <Th align="right">AI visibility</Th>
                </tr>
              </THead>
              <tbody>
                {markets.map((m) => (
                  <Tr key={m.countryCode}>
                    <Td className="text-text-primary">{countryName(m.countryCode)}</Td>
                    <Td align="right">
                      {m.visibility === null ? (
                        <span className="text-text-tertiary">Not tracked in Peec</span>
                      ) : (
                        pctOrDash(m.visibility)
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            {(unmentioned.length > 0 || untracked.length > 0) && (
              <TableFooter>
                {[
                  unmentioned.length > 0 &&
                    `${unmentioned.map((m) => countryName(m.countryCode)).join(', ')}: 0% means Peec ran that market’s prompts and no AI answer mentioned SEQUESTO.`,
                  untracked.length > 0 &&
                    `${untracked.map((m) => countryName(m.countryCode)).join(', ')}: Peec ran no prompts for ${untracked.length === 1 ? 'this market' : 'these markets'} in the period, so there is nothing to measure; add them in Peec to track.`,
                ]
                  .filter(Boolean)
                  .join(' ')}
              </TableFooter>
            )}
            </>
          ) : (
            <ChartEmpty label="No per-market data." />
          )}
        </div>
      </div>
    </Card>
  );
}

/** Share of search compares something only with SEQUESTO, a competitor and some volume. */
function shareOfSearchUsable(rows: NonNullable<GtmBoardData['brandSplit']>['shareOfSearch'] | undefined): boolean {
  return !!rows && rows.some((s) => s.isOwn) && rows.some((s) => !s.isOwn) && rows.some((s) => s.share > 0);
}

function BrandSplit({ data }: { data: GtmBoardData }) {
  if (!data.brandSplit) {
    return (
      <Card>
        <CardHeader title="Brand vs category" description="Branded vs non-branded organic clicks (GSC)" />
        <ChartEmpty label="No Search Console data." />
      </Card>
    );
  }
  const { branded, nonBranded, brandedClickShare, trend, shareOfSearch } = data.brandSplit;
  const named = branded.clicks + nonBranded.clicks;
  // The headline tile counts every click; this card can only split the ones with a named query.
  const allClicks = data.searchTrend ? data.searchTrend.points.reduce((n, p) => n + p.clicks, 0) : null;
  const anonymised = allClicks !== null ? Math.max(0, allClicks - named) : null;
  const daily = sevenDayAverage(trend ?? [], ['nonBrandedClicks', 'brandedClicks'], (trend?.length ?? 0) > SMOOTH_MIN_POINTS ? 7 : 1);
  return (
    <Card>
      <CardHeader
        title="Brand vs category"
        description={
          shareOfSearchUsable(shareOfSearch)
            ? 'Branded vs non-branded organic clicks & share of search (GSC + DataForSEO)'
            : 'Branded vs non-branded organic clicks (GSC)'
        }
      />
      <div className="grid grid-cols-2 gap-6 mb-4">
        <InlineStat label="Branded clicks" value={fmtNumber(branded.clicks)} />
        <InlineStat label="Non-branded clicks" value={fmtNumber(nonBranded.clicks)} />
      </div>
      {/* Same colours as the line chart below: non-branded ink, branded stone. */}
      <div className="h-3 w-full overflow-hidden flex" style={{ backgroundColor: CHART_SERIES[0] }}>
        <div
          className="h-full"
          style={{ width: `${brandedClickShare * 100}%`, backgroundColor: CHART_SERIES[2] }}
        />
      </div>
      <p className="text-body-xs text-text-tertiary mt-2">
        {fmtPercent(brandedClickShare * 100)} branded: category demand (non-branded) is the leading indicator to grow.
      </p>
      {anonymised !== null && anonymised > 0 && (
        <p className="text-body-xs text-text-tertiary mt-1">
          {fmtNumber(named)} clicks on named queries + {fmtNumber(anonymised)} on queries Google anonymises ={' '}
          {fmtNumber(allClicks!)} organic clicks (the headline tile). Anonymised queries can’t be split into brand and
          category.
        </p>
      )}

      {daily.length > 0 && (
        <div className="mt-6">
          <ChartSubtitle>{(trend?.length ?? 0) > SMOOTH_MIN_POINTS ? 'Clicks per day, 7-day average' : 'Clicks per day'}</ChartSubtitle>
          <ResponsiveContainer width="100%" height={CHART_HEIGHT.compact}>
            <LineChart data={daily} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid {...CHART_GRID} />
              <XAxis dataKey="date" {...CHART_AXIS} minTickGap={32} tickFormatter={axisDate} />
              <YAxis {...CHART_AXIS} tickFormatter={(v: number) => fmtNumber(Math.round(v))} />
              <Tooltip {...CHART_TOOLTIP} labelFormatter={tooltipDate} formatter={(v) => fmtNumber(Math.round(Number(v) * 10) / 10)} />
              {/* Line keys for line series, not dots. */}
              <Legend {...CHART_LEGEND} iconType="plainline" iconSize={14} />
              <Line type="linear" dataKey="nonBrandedClicks" name="Non-branded" stroke={CHART_SERIES[0]} dot={false} strokeWidth={2} isAnimationActive={false} />
              <Line type="linear" dataKey="brandedClicks" name="Branded" stroke={CHART_SERIES[2]} dot={false} strokeWidth={2} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* A lone brand, no SEQUESTO row, or every share at 0 compares nothing: leave the block out and say why. */}
      {!shareOfSearchUsable(shareOfSearch) && (
        <p className="mt-6 text-body-xs text-text-tertiary">
          Share of search vs competitors is hidden: it needs brand search volumes from DataForSEO, which has stored
          none (on 13 Oct its API refused every market with 402, payment required). Integrations shows the sync’s state.
        </p>
      )}
      {shareOfSearch && shareOfSearchUsable(shareOfSearch) && (
        <div className="mt-6">
          <ChartSubtitle>Share of search vs competitors (brand-name volume)</ChartSubtitle>
          <BarListChart
            items={shareOfSearch.slice(0, 6).map((s) => ({
              label: s.brand,
              value: s.share * 100,
              highlight: s.isOwn,
            }))}
            colorMode="highlight"
            color={CHART_SERIES[1]}
            formatValue={(v) => fmtPercent(v, 0)}
            labelWidth="w-28"
          />
        </div>
      )}
    </Card>
  );
}

/** Pipeline created, summed across channels: open + won, and the lost value left out. */
function pipelineTotals(rows: NonNullable<GtmBoardData['sourcedPipeline']>) {
  return {
    pipeline: rows.reduce((n, r) => n + r.pipelineValue, 0),
    deals: rows.reduce((n, r) => n + r.dealCount, 0),
    lost: rows.reduce((n, r) => n + (r.lostValue ?? 0), 0),
  };
}

function IcpByVertical({ data }: { data: GtmBoardData }) {
  if (!data.icpByVertical || data.icpByVertical.rows.length === 0) {
    return (
      <Card>
        <CardHeader title="ICP fit by category" description="Pipeline created and form leads by Attio’s Industry / ICP" />
        <ChartEmpty label="No pipeline or leads in this period." />
      </Card>
    );
  }
  const { rows, icpFitPipelineShare, icpFitLeadShare, unclassifiedPipelineShare, unclassifiedLeadShare } = data.icpByVertical;
  const pipeline = rows.reduce((n, r) => n + r.pipelineValue, 0);
  const deals = rows.reduce((n, r) => n + r.dealCount, 0);
  const leads = rows.reduce((n, r) => n + r.leadCount, 0);
  const icpLeads = rows.filter((r) => r.fit === 'icp').reduce((n, r) => n + r.leadCount, 0);
  const unclassifiedNote = (share: number | undefined) =>
    share === undefined ? '' : ` · ${fmtPercent(share * 100, 0)} unclassified`;
  // More than half with no category: the shares say little until Attio is filled in.
  // Name which side: on 12 Oct 56% of leads but only 24% of pipeline had none.
  const unclassifiedLabel = mostlyUnclassifiedLabel(unclassifiedPipelineShare, unclassifiedLeadShare);
  return (
    <Card>
      <CardHeader
        title="ICP fit by category"
        description="Pipeline created and form leads, by the company’s Industry / ICP in Attio"
        action={unclassifiedLabel ? <Badge variant="warning">{unclassifiedLabel}</Badge> : undefined}
      />
      <div className="grid grid-cols-2 gap-6 mb-4">
        <InlineStat
          label="ICP-fit pipeline"
          value={fmtPercent(icpFitPipelineShare * 100)}
          sub={`of ${fmtCurrency(pipeline)} created${unclassifiedNote(unclassifiedPipelineShare)}`}
        />
        <InlineStat
          label="ICP-fit leads"
          value={fmtPercent(icpFitLeadShare * 100)}
          sub={`${fmtNumber(icpLeads)} of ${fmtNumber(leads)} leads${unclassifiedNote(unclassifiedLeadShare)}`}
        />
      </div>
      <Table colWidths={['auto', '6.5rem', '4rem', '4rem']}>
        <THead>
          <tr>
            <Th>ICP category</Th>
            <Th align="right">Pipeline</Th>
            <Th align="right">Deals</Th>
            <Th align="right">Leads</Th>
          </tr>
        </THead>
        <tbody>
          {rows.map((r) => (
            <Tr key={r.vertical}>
              <Td className={r.fit === 'icp' ? 'text-text-primary' : 'text-text-tertiary'}>
                <span className="block break-words">{r.vertical}</span>
              </Td>
              <Td align="right">{r.pipelineValue === 0 ? <Nil /> : fmtCurrency(r.pipelineValue)}</Td>
              <Td align="right">{r.dealCount === 0 ? <Nil /> : fmtNumber(r.dealCount)}</Td>
              <Td align="right">{r.leadCount === 0 ? <Nil /> : fmtNumber(r.leadCount)}</Td>
            </Tr>
          ))}
          <TTotalRow>
            <Td>Total</Td>
            <Td align="right">{fmtCurrency(pipeline)}</Td>
            <Td align="right">{fmtNumber(deals)}</Td>
            <Td align="right">{fmtNumber(leads)}</Td>
          </TTotalRow>
        </tbody>
      </Table>
      <TableFooter>
        ICP fit = a ratified ICP category in Attio’s Industry / ICP; both shares are the ICP rows over the total row,
        the same totals as “Pipeline created” and the funnel’s leads. Unclassified = no category (or a pre-remap
        value), no company on the deal, or a lead with no matching Attio company.
      </TableFooter>
    </Card>
  );
}

/**
 * Sources name countries differently (GSC "FI", GA4 "Finland", some "SINGAPORE"):
 * merge rows that land on the same name, then rank by clicks, then sessions.
 */
function mergedMarkets(rows: NonNullable<GtmBoardData['markets']>) {
  const byName = new Map<string, { country: string; clicks: number; impressions: number; sessions: number }>();
  for (const r of rows) {
    const name = countryName(r.country);
    const cur = byName.get(name) ?? { country: name, clicks: 0, impressions: 0, sessions: 0 };
    cur.clicks += r.clicks;
    cur.impressions += r.impressions;
    cur.sessions += r.sessions;
    byName.set(name, cur);
  }
  // The API sums the long tail into one "Other countries (n)" row: keep it last.
  const isRest = (name: string) => name.startsWith('Other countries');
  return [...byName.values()].sort(
    (x, y) => Number(isRest(x.country)) - Number(isRest(y.country)) || y.clicks - x.clicks || y.sessions - x.sessions,
  );
}

function Markets({ data }: { data: GtmBoardData }) {
  if (!data.markets || data.markets.length === 0) {
    return (
      <Card>
        <CardHeader title="Multi-market footprint" description="Clicks & sessions by country (GSC + GA4)" />
        <ChartEmpty label="No market data for this period." />
      </Card>
    );
  }
  const rows = mergedMarkets(data.markets);
  const clicks = rows.reduce((n, r) => n + r.clicks, 0);
  const impressions = rows.reduce((n, r) => n + r.impressions, 0);
  const sessions = rows.reduce((n, r) => n + r.sessions, 0);
  const allClicks = data.searchTrend ? data.searchTrend.points.reduce((n, p) => n + p.clicks, 0) : null;
  return (
    <Card>
      <CardHeader title="Multi-market footprint" description="Clicks & sessions by country (GSC + GA4)" />
      <Table colWidths={['auto', '5rem', '6.5rem', '5.5rem']}>
        <THead>
          <tr>
            <Th>Country</Th>
            <Th align="right">Clicks</Th>
            <Th align="right">Impressions</Th>
            <Th align="right">Sessions</Th>
          </tr>
        </THead>
        <tbody>
          {rows.map((m) => (
            <Tr key={m.country}>
              <Td className={m.country.startsWith('Other countries') ? 'text-text-tertiary' : 'text-text-primary'}>{m.country}</Td>
              <Td align="right">{fmtNumber(m.clicks)}</Td>
              <Td align="right">{fmtNumber(m.impressions)}</Td>
              <Td align="right">{fmtNumber(m.sessions)}</Td>
            </Tr>
          ))}
          <TTotalRow>
            <Td>All countries</Td>
            <Td align="right">{fmtNumber(clicks)}</Td>
            <Td align="right">{fmtNumber(impressions)}</Td>
            <Td align="right">{fmtNumber(sessions)}</Td>
          </TTotalRow>
        </tbody>
      </Table>
      <TableFooter>
        {allClicks === null || allClicks === clicks
          ? 'Clicks match Search Console’s total for the period.'
          : `${fmtNumber(clicks)} clicks with a country vs ${fmtNumber(allClicks)} in all (the headline tile): Search Console drops some clicks from its by-country report for privacy.`}{' '}
        Sessions are GA4’s.
      </TableFooter>
    </Card>
  );
}
